// BUILD-046 pass F1 (Owner direction 2026-10-25): a dropdown that cannot be clipped. The compact Edit task form sits inside the task section (and, for a task in My Tasks, inside the table box whose overflow is
// hidden), so a results list that is positioned inside it would be cut off. A floating list is drawn on the page body at fixed coordinates against its anchor (below it, or above it when there is no room),
// kept in step on scroll and resize - the same approach as the row's More actions menu. The Assign task dialog keeps its in-place list (`floating` false). Keyboard use is unchanged: the owner of the list is the
// combobox input, which keeps focus; the list is only drawn elsewhere.
import { useLayoutEffect, useState } from 'react';
import type { CSSProperties, ReactNode, RefObject } from 'react';
import { createPortal } from 'react-dom';
import { placeFloating } from './floatingPlacementModel';
import type { FloatingPlacement } from './floatingPlacementModel';

export interface FloatingPanelProps {
  anchorRef: RefObject<HTMLElement | null>;
  children: ReactNode;
  className?: string;
  /** Wanted height before the room left on the page limits it. */
  wantHeight?: number;
  /** Narrowest the panel may be when the anchor is narrower (long names wrap inside it). */
  minWidth?: number;
  id?: string;
  role?: string;
  ariaLabel?: string;
  testId?: string;
  onMouseDown?: (event: React.MouseEvent<HTMLDivElement>) => void;
}

export function FloatingPanel({ anchorRef, children, className = '', wantHeight = 280, minWidth = 280, id, role, ariaLabel, testId, onMouseDown }: FloatingPanelProps) {
  const [place, setPlace] = useState<FloatingPlacement | null>(null);
  useLayoutEffect(() => {
    const measure = (): void => {
      const el = anchorRef.current;
      if (el === null) return;
      const r = el.getBoundingClientRect();
      setPlace(placeFloating({ left: r.left, top: r.top, bottom: r.bottom, width: r.width }, { width: window.innerWidth, height: window.innerHeight }, { height: wantHeight, minWidth }));
    };
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [anchorRef, wantHeight, minWidth]);
  const style: CSSProperties = place === null
    ? { position: 'fixed', top: 0, left: 0, opacity: 0, pointerEvents: 'none' }
    : { position: 'fixed', left: place.left, width: place.width, maxHeight: place.maxHeight, ...(place.above ? { bottom: place.bottom } : { top: place.top }), overflowY: 'auto' };
  return createPortal(
    <div id={id} role={role} aria-label={ariaLabel} data-testid={testId} data-placement={place === null ? undefined : place.above ? 'above' : 'below'} style={style} onMouseDown={onMouseDown} className={`z-50 ${className}`}>
      {children}
    </div>,
    document.body
  );
}

/** The results list of a combobox: in place (absolute, the width of its field) or floating (see above). The wrapper keeps the pointer from taking focus out of the input. */
export function DropdownShell({ floating, anchorRef, children }: { floating: boolean; anchorRef: RefObject<HTMLElement | null>; children: ReactNode }) {
  const look = 'rounded border border-gray-200 bg-white shadow-lg';
  const keepFocus = (event: React.MouseEvent<HTMLDivElement>): void => event.preventDefault();
  return floating ? (
    <FloatingPanel anchorRef={anchorRef} className={look} onMouseDown={keepFocus} testId="floating-list">
      {children}
    </FloatingPanel>
  ) : (
    <div className={`absolute left-0 right-0 z-40 mt-1 ${look}`} onMouseDown={keepFocus}>
      {children}
    </div>
  );
}
